import { RESOURCES, ResourceKey } from "@/constants/resources";
import {
  actionNoun,
  countLabel,
  doneLabel,
} from "@/helpers/ResourceHelpers";
import {
  ActionJob,
  ActionJobStatus,
  isActiveActionJob,
} from "@/interfaces/ActionJob";
import {
  useCreateActionJobMutation,
  useGetActionJobsQuery,
  useMarkActionJobsSeenMutation,
} from "@/services/actionJobs";
import api from "@/services/api";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

export const POLLING_INTERVAL_MS = 2000;
//* Durée pendant laquelle un job terminé masque encore ses lignes, le temps que la liste se recharge
export const SETTLE_MS = 3000;

interface IActionJobsContext {
  jobs: ActionJob[];
  //* Ids à masquer dans les tableaux de cette ressource : engagés dans un job en cours
  hiddenIds: (resource: ResourceKey) => Set<string>;
  //* Lance une suppression en masse ; `false` si l'API l'a refusée (toast déjà affiché)
  startBulkDelete: (resource: ResourceKey, ids: string[]) => Promise<boolean>;
  markSeen: () => void;
  //* Supprimer en masse est réservé aux ADMIN et CSM
  canBulkDelete: boolean;
}

const EMPTY_IDS = new Set<string>();

//* Valeur par défaut : un tableau rendu hors du layout (tests) n'a aucun job
export const ActionJobsContext = createContext<IActionJobsContext>({
  jobs: [],
  hiddenIds: () => EMPTY_IDS,
  startBulkDelete: async () => false,
  markSeen: () => undefined,
  canBulkDelete: false,
});

export const useActionJobs = () => useContext(ActionJobsContext);

const notifyFinished = (job: ActionJob) => {
  const config = RESOURCES[job.resource];
  if (!config) return;

  if (job.status === "failed") {
    //* Une action interrompue en cours de route a pu en faire une partie
    toast.error(
      job.succeeded > 0
        ? `${actionNoun(config)} de ${countLabel(config, job.total)} interrompue : ${countLabel(
            config,
            job.succeeded,
          )} ${doneLabel(config, job.succeeded)}. ${job.error ?? ""}`.trim()
        : `${actionNoun(config)} de ${countLabel(config, job.total)} impossible : ${
            job.error ?? "erreur inattendue."
          }`,
    );
  } else if (job.failed > 0) {
    toast.warning(
      `${countLabel(config, job.succeeded)} ${doneLabel(config, job.succeeded)} · ${
        job.failed
      } en échec — détail dans les tâches`,
    );
  } else {
    toast.success(
      `${countLabel(config, job.succeeded)} ${doneLabel(config, job.succeeded)}`,
    );
  }
};

interface ISettling {
  resource: ResourceKey;
  ids: string[];
}

export const ActionJobsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const canBulkDelete = checkIfUserHasRoles(user, ["ADMIN", "CSM"]);
  const [hasActive, setHasActive] = useState(false);
  const { data: jobs = [], refetch } = useGetActionJobsQuery(undefined, {
    pollingInterval: hasActive ? POLLING_INTERVAL_MS : 0,
  });
  const [createActionJob] = useCreateActionJobMutation();
  const [markActionJobsSeen] = useMarkActionJobsSeenMutation();

  const previousStatuses = useRef(new Map<string, ActionJobStatus>());
  const knownTargets = useRef(new Map<string, string[]>());
  const [settling, setSettling] = useState<Map<string, ISettling>>(new Map());
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  //* Pas de mise à jour d'état après le démontage
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  //* Retour sur l'onglet : on relit la liste sans attendre le prochain polling
  useEffect(() => {
    const onFocus = () => {
      refetch();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refetch]);

  useEffect(() => {
    setHasActive(jobs.some(isActiveActionJob));

    jobs.forEach((job) => {
      if (isActiveActionJob(job) && job.targetIds) {
        knownTargets.current.set(job.id, job.targetIds);
      }

      const previous = previousStatuses.current.get(job.id);
      previousStatuses.current.set(job.id, job.status);
      if (!previous || !isActiveActionJob({ status: previous })) return;
      if (isActiveActionJob(job)) return;

      //* Le job vient de se terminer pendant cette session
      notifyFinished(job);
      dispatch(api.util.invalidateTags(RESOURCES[job.resource]?.tags as any));

      const ids = knownTargets.current.get(job.id) ?? [];
      setSettling((old) => new Map(old).set(job.id, { resource: job.resource, ids }));
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        setSettling((old) => {
          const next = new Map(old);
          next.delete(job.id);
          return next;
        });
      }, SETTLE_MS);
      timers.current.add(timer);
    });
  }, [jobs, dispatch]);

  const hiddenByResource = useMemo(() => {
    const map = new Map<ResourceKey, Set<string>>();
    const hide = (resource: ResourceKey, ids: string[]) => {
      const set = map.get(resource) ?? new Set<string>();
      ids.forEach((id) => set.add(id));
      map.set(resource, set);
    };
    jobs
      .filter(isActiveActionJob)
      .forEach((job) => hide(job.resource, job.targetIds ?? []));
    settling.forEach(({ resource, ids }) => hide(resource, ids));
    return map;
  }, [jobs, settling]);

  const hiddenIds = useCallback(
    (resource: ResourceKey) => hiddenByResource.get(resource) ?? EMPTY_IDS,
    [hiddenByResource],
  );

  const startBulkDelete = useCallback(
    async (resource: ResourceKey, ids: string[]) => {
      const config = RESOURCES[resource];
      try {
        await createActionJob({ type: "bulk_delete", resource, ids }).unwrap();
        toast.info(
          `${actionNoun(config)} de ${countLabel(config, ids.length)} lancée`,
        );
        return true;
      } catch (error: any) {
        toast.error(
          error?.status === 403
            ? `Vous n'avez pas le droit ${
                config.effect === "archive" ? "d'archiver" : "de supprimer"
              } des ${config.plural}.`
            : error?.status === 422
              ? `${actionNoun(config)} impossible : la sélection n'est pas valide (10 000 lignes au plus).`
              : `${actionNoun(config)} impossible à lancer. Réessayez.`,
        );
        return false;
      }
    },
    [createActionJob],
  );

  const markSeen = useCallback(() => {
    const hasUnseen = jobs.some(
      (job) => !isActiveActionJob(job) && !job.seenAt,
    );
    if (hasUnseen) markActionJobsSeen();
  }, [jobs, markActionJobsSeen]);

  const value = useMemo(
    () => ({ jobs, hiddenIds, startBulkDelete, markSeen, canBulkDelete }),
    [jobs, hiddenIds, startBulkDelete, markSeen, canBulkDelete],
  );

  return (
    <ActionJobsContext.Provider value={value}>
      {children}
    </ActionJobsContext.Provider>
  );
};
